import {
  Links,
  LoaderFunctionArgs,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteLoaderData,
  Meta,
  useLocation,
  LinksFunction,
  redirect
} from 'react-router'
import {Toaster, TooltipProvider} from '@translated/babele-react'
import {useEffect} from 'react'
import {loadGtmScript} from '~/utils/scripts'
import {getLocalizedPath, getPathDataFromRequest} from '~/utils/routes'
import {getAppSearchParams, getWebsiteSearchParams, getSearchStringWithoutParams} from './utils/url'
import {HubspotLauncher} from './features/HubspotLauncher/HubspotLauncher'
import {dir as getLangDirection} from 'i18next'
import {useTranslation} from 'react-i18next'
import {DirectionProvider} from '@radix-ui/react-direction'
import {checkAuth} from './utils/auth'
import {SupportedLanguage} from './utils/locales'
import {ROOT_LINKS} from '~/utils/links'
import {envVars} from './utils/env.server'

export const links: LinksFunction = () => ROOT_LINKS

export const loader = ({request}: LoaderFunctionArgs) => {
  const url = new URL(request.url)
  const protocol = request.headers.get('x-forwarded-proto') || 'http'
  url.protocol = protocol
  const {search} = url

  const pathData = getPathDataFromRequest(request)
  const uiDirection = getLangDirection(pathData.language)

  /*
   * TODO: re-enable this check once the GTM team decides to go live with footer UI:
   * const shouldDisplayFooter = ['it', 'en'].includes(pathData.language)
   */
  const shouldDisplayFooter = false

  const isAuthenticated = checkAuth(request)
  const {lng: languageFromSearch} = getWebsiteSearchParams(search)
  const {sourceApp} = getAppSearchParams(search)

  if (!languageFromSearch)
    return {
      pathData,
      uiDirection,
      shouldDisplayFooter,
      isAuthenticated,
      requestUrl: url,
      comingFromMymemory: sourceApp === 'mymemory',
      env: {
        isHubspotEnabled: envVars.VITE_IS_HUBSPOT_ENABLED,
        gaTrackingId: envVars.VITE_GA_TRACKING_ID,
        isGaEnabled: envVars.VITE_IS_GA_ENABLED,
        laraAppUrl: envVars.VITE_LARA_APP_URL
      }
    }

  const localizedPath = getLocalizedPath({
    path: pathData.currentPath,
    language: languageFromSearch as SupportedLanguage
  })

  if (localizedPath) {
    const searchString = getSearchStringWithoutParams(search, ['lng'])
    return redirect(`${encodeURI(localizedPath)}${searchString}`)
  }
}

export function Layout({children}: {children: React.ReactNode}) {
  const {pathData, uiDirection, requestUrl, comingFromMymemory, env} =
    useRouteLoaderData<typeof loader>('root') || {}

  const location = useLocation()
  const skipPageIndexing = !!getAppSearchParams(location.search).text

  useEffect(() => {
    if (env?.gaTrackingId != null && env?.isGaEnabled) loadGtmScript(env.gaTrackingId)
  }, [env?.gaTrackingId, env?.isGaEnabled])

  let url: URL | undefined
  let baseUrl

  if (requestUrl) {
    url = new URL(requestUrl)
    baseUrl = `${url.protocol}//${url.host}`
  }

  return (
    <html
      data-theme="translated-light"
      data-panda-theme="translated"
      data-panda-mode="light"
      lang={pathData?.language || 'en'}
      dir={uiDirection}
      className="h-full bg-[#4445B8] text-light-icons-primary 4xl:text-[18px] 5xl:text-[32px]"
    >
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="manifest" href="/manifest.json" />
        {skipPageIndexing && <meta name="robots" content="noindex, follow" />}
        {url && <link rel="canonical" href={`${baseUrl}${url.pathname}`} />}
        <Links />
        <Meta />
      </head>
      <body className="bg-laraFull relative flex min-h-screen flex-col items-stretch text-neutral-1000">
        <TooltipProvider>{children}</TooltipProvider>
        <Toaster position="top-right" />
        <ScrollRestoration />
        <Scripts />
        {env?.isHubspotEnabled && !comingFromMymemory && <HubspotLauncher />}
      </body>
    </html>
  )
}

export default function App() {
  const {i18n} = useTranslation()

  return (
    <DirectionProvider dir={i18n.dir()}>
      <Outlet />
    </DirectionProvider>
  )
}

export {ErrorBoundary} from '~/features/RootErrorBoundary'
